import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  BookOpen, Calendar, Clock, User, FileText, Upload, Check, 
  AlertCircle, ChevronLeft, Send, Paperclip, X, Download,
  File, Image, Video, Music, Archive, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get icon based on file type
const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  const videoExts = ['mp4', 'mov', 'avi', 'wmv', 'webm'];
  const audioExts = ['mp3', 'wav', 'ogg', 'aac'];
  const archiveExts = ['zip', 'rar', '7z', 'tar', 'gz'];
  
  if (imageExts.includes(ext)) return Image;
  if (videoExts.includes(ext)) return Video;
  if (audioExts.includes(ext)) return Music;
  if (archiveExts.includes(ext)) return Archive;
  return File;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const AssignmentSubmission = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [content, setContent] = useState("");
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (assignmentId) {
      fetchAssignment();
      fetchExistingSubmission();
    }
  }, [assignmentId, token]);

  const fetchAssignment = async () => {
    try {
      const data = await apiCall("GET", `/assignments/${assignmentId}`, null, token);
      setAssignment(data);
    } catch (err) {
      toast.error("Failed to load assignment");
      navigate("/dashboard");
    }
    setLoading(false);
  };

  const fetchExistingSubmission = async () => {
    try {
      const submissions = await apiCall("GET", `/submissions?assignment_id=${assignmentId}`, null, token);
      if (submissions && submissions.length > 0) {
        setExistingSubmission(submissions[0]);
        setContent(submissions[0].content || "");
      }
    } catch (err) {
      console.log("No existing submission");
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFiles = (newFiles) => {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const validFiles = newFiles.filter(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Max size is 50MB`);
        return false;
      }
      return true;
    });

    if (files.length + validFiles.length > 10) {
      toast.error("Maximum 10 files allowed");
      return;
    }

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (file, index) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'submission');
    formData.append('related_id', assignmentId);

    try {
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      setUploadProgress(prev => ({ ...prev, [index]: 100 }));
      return result;
    } catch (error) {
      setUploadProgress(prev => ({ ...prev, [index]: -1 }));
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!content.trim() && files.length === 0 && uploadedFiles.length === 0) {
      toast.error("Please add content or upload files");
      return;
    }

    setSubmitting(true);

    try {
      // Upload all pending files first
      const uploadPromises = files.map(async (file, index) => {
        setUploadProgress(prev => ({ ...prev, [index]: 0 }));
        return uploadFile(file, index);
      });

      const uploadResults = await Promise.all(uploadPromises);
      const allFiles = [...uploadedFiles, ...uploadResults.filter(r => r)];

      // Submit assignment
      const fileUrls = allFiles.map(f => f.url || f.id).join(',');
      
      await apiCall("POST", "/submissions", {
        assignment_id: assignmentId,
        content: content,
        file_url: fileUrls
      }, token);

      toast.success("Assignment submitted successfully!");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Failed to submit assignment");
    }
    
    setSubmitting(false);
  };

  const isOverdue = assignment && new Date(assignment.due_date) < new Date();
  const daysUntilDue = assignment ? Math.ceil((new Date(assignment.due_date) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 flex items-center justify-center">
        <Card className="bg-white p-8 text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-bold text-gray-700">Assignment not found</h2>
          <Button className="mt-4" onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="assignment-submission">
      {/* Header */}
      <div className="bg-[#002147] text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white mb-4"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft size={20} /> Back
          </Button>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-[#FF6B00] rounded-xl flex items-center justify-center">
              <BookOpen size={28} />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold">{assignment.title}</h1>
              <p className="text-white/80">{assignment.subject}</p>
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm">
                <span className="flex items-center gap-1">
                  <User size={14} /> {assignment.teacher_name}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={14} /> Due: {new Date(assignment.due_date).toLocaleDateString()}
                </span>
                <Badge className={isOverdue ? "bg-red-500" : daysUntilDue <= 3 ? "bg-orange-500" : "bg-green-500"}>
                  {isOverdue ? "Overdue" : daysUntilDue <= 0 ? "Due Today" : `${daysUntilDue} days left`}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Assignment Details */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Assignment Details</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 whitespace-pre-wrap">{assignment.description}</p>
                <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#FF6B00]">{assignment.max_points}</p>
                    <p className="text-xs text-gray-500">Max Points</p>
                  </div>
                  {assignment.class_id && (
                    <div className="text-center">
                      <p className="text-sm font-medium text-[#002147]">{assignment.class_id}</p>
                      <p className="text-xs text-gray-500">Class</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Existing Submission */}
            {existingSubmission && (
              <Card className="bg-green-50 border-green-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <Check size={20} /> Previous Submission
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-2">
                    Submitted on {new Date(existingSubmission.submitted_at).toLocaleString()}
                  </p>
                  {existingSubmission.grade !== null && (
                    <div className="bg-white p-3 rounded-lg">
                      <p className="font-semibold text-[#002147]">
                        Grade: {existingSubmission.grade}/{assignment.max_points}
                      </p>
                      {existingSubmission.feedback && (
                        <p className="text-sm text-gray-600 mt-2">
                          <strong>Feedback:</strong> {existingSubmission.feedback}
                        </p>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    You can resubmit to update your assignment
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Submission Form */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Your Submission</CardTitle>
                <CardDescription>
                  Write your response and/or upload supporting files
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Text Response */}
                <div>
                  <Textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Write your assignment response here..."
                    rows={8}
                    className="resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">
                    {content.length} characters
                  </p>
                </div>

                {/* File Upload Area */}
                <div
                  className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
                    dragActive 
                      ? 'border-[#FF6B00] bg-orange-50' 
                      : 'border-gray-300 hover:border-[#FF6B00] hover:bg-orange-50/50'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-input').click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    onChange={(e) => handleFiles(Array.from(e.target.files))}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.mp4,.mp3,.zip,.pptx,.xlsx"
                  />
                  
                  <Upload className={`mx-auto mb-3 ${dragActive ? 'text-[#FF6B00]' : 'text-gray-400'}`} size={36} />
                  <p className="text-gray-600 font-medium">
                    {dragActive ? 'Drop files here' : 'Drag & drop files or click to browse'}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    PDF, DOC, DOCX, TXT, PNG, JPG, MP4, ZIP (Max 50MB each, 10 files max)
                  </p>
                </div>

                {/* Selected Files */}
                {files.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-medium text-[#002147]">Files to Upload ({files.length})</p>
                    {files.map((file, index) => {
                      const FileIcon = getFileIcon(file.name);
                      const progress = uploadProgress[index];
                      
                      return (
                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <FileIcon className="text-[#FF6B00] flex-shrink-0" size={24} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                            <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                            {progress !== undefined && progress >= 0 && (
                              <Progress value={progress} className="h-1 mt-1" />
                            )}
                            {progress === -1 && (
                              <p className="text-xs text-red-500 mt-1">Upload failed</p>
                            )}
                          </div>
                          {progress === 100 ? (
                            <Check className="text-green-500" size={20} />
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                              disabled={submitting}
                            >
                              <X size={16} className="text-gray-400 hover:text-red-500" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Uploaded Files */}
                {uploadedFiles.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-medium text-green-700">Uploaded Files</p>
                    {uploadedFiles.map((file, index) => {
                      const FileIcon = getFileIcon(file.filename);
                      return (
                        <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                          <FileIcon className="text-green-600 flex-shrink-0" size={24} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">{file.filename}</p>
                            <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                          </div>
                          <Check className="text-green-500" size={20} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Submit Button */}
            <Card className="bg-white sticky top-4">
              <CardContent className="p-4">
                <Button 
                  className="w-full bg-[#FF6B00] hover:bg-[#E65C00] mb-3" 
                  size="lg"
                  onClick={handleSubmit}
                  disabled={submitting || isOverdue}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" /> Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" /> Submit Assignment
                    </>
                  )}
                </Button>
                
                {isOverdue && (
                  <p className="text-xs text-red-500 text-center">
                    This assignment is overdue. Contact your teacher for late submissions.
                  </p>
                )}
                
                <div className="text-center mt-4">
                  <p className="text-xs text-gray-500">
                    {content.length > 0 && `${content.length} characters`}
                    {content.length > 0 && files.length > 0 && " • "}
                    {files.length > 0 && `${files.length} file(s)`}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-blue-700">Submission Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-xs text-blue-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <Check size={12} className="mt-0.5 flex-shrink-0" />
                    Review your work before submitting
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={12} className="mt-0.5 flex-shrink-0" />
                    Include your name in uploaded files
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={12} className="mt-0.5 flex-shrink-0" />
                    Check file formats are supported
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={12} className="mt-0.5 flex-shrink-0" />
                    You can resubmit until the deadline
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentSubmission;
